export function createGalerieFeature({
  dashboardItems,
  galerieAnimPreview,
  galerieAnimFull,
  galerieEmptyEl,
  drawLine,
  animSpeed,
  isDbLoaded,
  isGalerieTabActive,
  onRebuildDone,
}) {
  let galerieLines = [];
  let dashboardOrderedLines = [];
  let galerieFrameId = null;
  let galerieLast = null;
  let galerieResizeObs = null;
  let galerieIntersectionObs = null;
  const visibleLineIds = new Set();
  let shuffleMode = false;

  function stop() {
    if (galerieFrameId) {
      cancelAnimationFrame(galerieFrameId);
      galerieFrameId = null;
    }
    galerieLast = null;
  }

  // Résolution plafonnée à x2 (écrans x3 des iPhone) : mesuré sur iPhone
  // (Safari, 28 septembre 2026), la composition des ~21 canvas visibles
  // pleine largeur prenait 15 ms par image à x3 -- avec le dessin, ~19,5 ms,
  // au-delà des 16,7 ms d'une image à 60 Hz (saccade régulière). x2 divise
  // par ~2,25 les pixels à composer, sans différence visible sur du texte
  // en mouvement.
  const GALERIE_MAX_DPR = 2;
  const VISIBLE_MARGIN_PX = 100;
  const PREDRAW_PER_FRAME = 2;

  // Réaffecter width/height efface le canvas, même à valeur égale : on ne
  // le fait que si la taille change, et on redessine aussitôt les lignes à
  // l'écran. Sinon, le ResizeObserver (appelé après le dessin de l'image,
  // juste avant l'affichage) laissait les lignes vides pendant 3 images --
  // flash à chaque reconstruction (nouvelle punchline reçue, bouton
  // aléatoire), mesuré le 31 octobre 2026.
  function resizeLineCanvas(line) {
    const dpr = Math.min(window.devicePixelRatio || 1, GALERIE_MAX_DPR);
    const width = window.innerWidth || 900;
    const heightPhys = Math.floor(line.data.canvasH / line.data.pw * width * dpr);
    const heightCss = Math.ceil(heightPhys / dpr);
    const widthPhys = width * dpr;
    if (line.canvas.width === widthPhys && line.canvas.height === heightPhys) return false;
    line.canvas.width = widthPhys;
    line.canvas.height = heightPhys;
    line.canvas.style.height = heightCss + 'px';
    line.drawn = false;
    return true;
  }

  function isOnScreen(canvas) {
    const rect = canvas.getBoundingClientRect();
    return rect.height > 0 && rect.bottom > -VISIBLE_MARGIN_PX && rect.top < window.innerHeight + VISIBLE_MARGIN_PX;
  }

  function drawGalerieLine(line) {
    drawLine(line);
    line.drawn = true;
  }

  function resizeAll() {
    const changed = galerieLines.filter((line) => resizeLineCanvas(line));
    if (!isGalerieTabActive()) return;
    changed.forEach((line) => {
      if (isOnScreen(line.canvas)) drawGalerieLine(line);
    });
  }

  function start() {
    stop();
    const lines = galerieLines;

    function loop(ts) {
      if (!galerieLast) galerieLast = ts;
      const dt = Math.min((ts - galerieLast) / 1000, 0.05);
      galerieLast = ts;
      let firstVisible = -1;
      let lastVisible = -1;

      lines.forEach((line, index) => {
        const pw = line.data.pw;
        if (line.dirRight) {
          line.tX += animSpeed * dt;
          if (line.tX >= pw) line.tX -= pw;
          line.rX -= animSpeed * dt;
          if (line.rX <= -pw) line.rX += pw;
        } else {
          line.tX -= animSpeed * dt;
          if (line.tX <= -pw) line.tX += pw;
          line.rX += animSpeed * dt;
          if (line.rX >= pw) line.rX -= pw;
        }
        if (visibleLineIds.has(line.id)) {
          drawGalerieLine(line);
          if (firstVisible < 0) firstVisible = index;
          lastVisible = index;
        }
      });

      // Les lignes voisines (à peu près un écran au-dessus et au-dessous)
      // sont dessinées une fois à l'avance, quelques-unes par image, pour
      // ne pas arriver vides au défilement rapide. Les lignes plus loin ne
      // sont pas dessinées (pas de mémoire graphique pour les ~120 canvas).
      // Voisinage pris dans l'ordre des lignes plutôt qu'avec une marge
      // d'IntersectionObserver : le conteneur qui défile (body, ou main en
      // plein écran) coupe la marge de l'observateur.
      if (firstVisible >= 0) {
        const reach = lastVisible - firstVisible + 1;
        let predrawBudget = PREDRAW_PER_FRAME;
        for (let distance = 1; distance <= reach && predrawBudget > 0; distance++) {
          for (const index of [lastVisible + distance, firstVisible - distance]) {
            const line = lines[index];
            if (predrawBudget > 0 && line && !line.drawn) {
              drawGalerieLine(line);
              predrawBudget--;
            }
          }
        }
      }

      galerieFrameId = requestAnimationFrame(loop);
    }

    galerieFrameId = requestAnimationFrame(loop);
  }

  function rebuild() {
    stop();
    if (galerieResizeObs) {
      galerieResizeObs.disconnect();
      galerieResizeObs = null;
    }
    if (galerieIntersectionObs) {
      galerieIntersectionObs.disconnect();
      galerieIntersectionObs = null;
    }
    visibleLineIds.clear();

    const prevPositions = new Map(galerieLines.map((line) => [line.id, { tX: line.tX, rX: line.rX }]));

    galerieAnimPreview.innerHTML = '';
    galerieLines = [];

    const loaded = isDbLoaded();
    galerieEmptyEl.hidden = loaded && dashboardItems.length > 0;
    galerieEmptyEl.classList.toggle('is-loading', !loaded);
    if (dashboardItems.length === 0) {
      onRebuildDone();
      return;
    }

    // Par défaut, ordre du dashboard (plus récente en haut, tri manuel
    // respecté). En mode aléatoire : la plus récente reste en haut, le reste
    // est remélangé à chaque reconstruction.
    const galerieItems = [dashboardItems[0]];
    if (dashboardItems.length > 1) {
      const rest = dashboardItems.slice(1);
      if (shuffleMode) {
        for (let i = rest.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [rest[i], rest[j]] = [rest[j], rest[i]];
        }
      }
      galerieItems.push(...rest);
    }

    galerieItems.forEach((item, index) => {
      const isOdd = index % 2 !== 0;
      const canvas = document.createElement('canvas');
      canvas.style.display = 'block';
      canvas.style.width = '100%';
      galerieAnimPreview.appendChild(canvas);

      const prev = prevPositions.get(item.id);
      galerieLines.push({
        id: item.id,
        canvas,
        ctx: canvas.getContext('2d'),
        data: item.data,
        bg: isOdd ? '#000000' : '#ffffff',
        fg: isOdd ? '#ffffff' : '#000000',
        dirRight: isOdd,
        tX: prev ? prev.tX : 0,
        rX: prev ? prev.rX : 0,
      });
    });

    resizeAll();

    const lineById = new Map(galerieLines.map(l => [l.id, l]));
    dashboardOrderedLines = dashboardItems.map((item, idx) => {
      const line = lineById.get(item.id);
      if (!line) return null;
      const isOdd = idx % 2 !== 0;
      return { ...line, bg: isOdd ? '#000000' : '#ffffff', fg: isOdd ? '#ffffff' : '#000000', dirRight: isOdd };
    }).filter(Boolean);

    if (typeof ResizeObserver !== 'undefined') {
      galerieResizeObs = new ResizeObserver(() => {
        resizeAll();
      });
      galerieResizeObs.observe(galerieAnimFull);
    }

    if (typeof IntersectionObserver !== 'undefined') {
      galerieIntersectionObs = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          const id = entry.target.dataset.lineId;
          if (entry.isIntersecting) {
            visibleLineIds.add(id);
          } else {
            visibleLineIds.delete(id);
          }
        }
      }, { rootMargin: `${VISIBLE_MARGIN_PX}px 0px` });

      galerieLines.forEach(line => {
        line.canvas.dataset.lineId = line.id;
        galerieIntersectionObs.observe(line.canvas);
      });
    } else {
      galerieLines.forEach(line => visibleLineIds.add(line.id));
    }

    if (isGalerieTabActive()) start();
    onRebuildDone();
  }

  function getLines() {
    return galerieLines;
  }

  function getDashboardLines() {
    return dashboardOrderedLines;
  }

  function isShuffleMode() {
    return shuffleMode;
  }

  function setShuffleMode(enabled) {
    shuffleMode = enabled;
    rebuild();
  }

  return {
    stop,
    start,
    resizeLineCanvas,
    resizeAll,
    rebuild,
    getLines,
    getDashboardLines,
    isShuffleMode,
    setShuffleMode,
  };
}
